<script setup lang="ts">
import IconItem from '@/components/Icon/IconItem.vue'
import { StackLayout } from '@/components/Layouts'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import TypographyWithPrefix from '@/components/Typography/TypographyWithPrefix.vue'
import { EColors } from '@/types/constants/ColorValues'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import DeviceRentalDateInformations from '../Detail/InformationsTab/components/DeviceRentalDateInformations.vue'
import DeviceOnlineStatus from '../Typography/DeviceOnlineStatus.vue'
import DeviceLastPingtime from './components/DeviceLastPingtime.vue'
import DeviceTimerEvent from './components/DeviceTimerEvent.vue'

type Props = {
    data: Partial<DeviceModel>
    showNextEvent?: boolean
    showNextCheckpoint?: boolean
    status?: AlertSocketMessageModel
}
defineProps<Props>()
</script>

<template>
    <StackLayout
        direction="column"
        class="device-card-content full-width full-height space-between">
        <StackLayout direction="column">
            <DeviceRentalDateInformations :data="data" />

            <TypographyWithPrefix
                v-if="showNextEvent && data.event"
                :prefix="$t('device.title.currentEventTitle')"
                :label="data.event?.name ?? ''"
                boldLabel />
            <TypographyItem
                v-else-if="data.event === null"
                :label="$t('device.title.noEventAssociated')" />
            <TypographyWithPrefix
                v-if="showNextCheckpoint && data.checkpoint"
                :prefix="$t('device.title.checkpointAssociatedTitle')"
                :label="data.checkpoint?.name ?? ''"
                boldLabel />
            <slot />
        </StackLayout>
        <StackLayout direction="column">
            <StackLayout direction="row" :gap="8">
                <DeviceOnlineStatus :status="status?.status ?? parseInt(data.status as string)" />
                <TypographyWithPrefix
                    v-if="!!status?.status && status?.signal && status.signal <= 2"
                    :label="$t('device.status.lowSignal')">
                    <IconItem icon="triangle-exclamation" :type="EColors.WARNING" />
                </TypographyWithPrefix>
            </StackLayout>
            <DeviceLastPingtime :devicePingTime="status?.pingtime ?? data?.pingtime" />
            <TypographyWithPrefix
                :prefix="$t('device.model.serialNumber')"
                :label="data.serial_number ?? ''"
                boldLabel />
            <DeviceTimerEvent :config="data.config" />
        </StackLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.device-card-content {
    & :deep(span) {
        font-size: small;
    }
    & :deep(svg) {
        width: 15px;
    }
}
</style>
